<template>
  <div class="product-card-container">
    <ProductThumbnail
      v-if="product"
      :key="productThumbnailKey"
      :product="product"
      :special-offers-badge="specialOffersBadge"
      :image-badge="imageBadge"
      :hide-review-text-and-rating="true"
      :skeleton="true"
      :lazy-load="index >= 4"
      :fetchpriority="index === 0 ? 'high' : 'auto'"
      class="product-item"
    />
    <div v-if="isWishlistOwner" class="wishlist-heart-small-card">
      <button :aria-label="title" :class="iconName" :title="title" @click="removeItem()"></button>
    </div>
    <div class="button-container grid-x margin-top-s">
      <div v-if="isAuthenticated && isWishlistOwner" class="cell small-12 medium-12 large-4 small-padding-right-z large-padding-right-xxs">
        <AtomicUiButton
          variant="secondary"
          :expanded="true"
          :loading="isPreparingToMove"
          :disabled="!available"
          @click="emit('move-item-click', item)"
        >
          Move
        </AtomicUiButton>
      </div>

      <div class="cell small-12 medium-12 large-8 small-padding-top-xxs large-padding-top-z">
        <AtomicUiButton variant="primary" :expanded="true" :loading="isAddToBagLoading" :disabled="!available" @click="handleProductAction">
          {{ buttonText }}
        </AtomicUiButton>
        <component :is="ATBComponent" @atb-closed="ATBComponent = null" />
      </div>
    </div>
  </div>
</template>

<script setup>
import addOrUpdateItemToBag from '@nuxt-common/add-to-bag/app/composables/useAddToBag';
import useSpecialOffersBadge from '@nuxt-common/product-thumbnail/app/composables/useSpecialOffersBadge.js';
import Toast from '@atomic-ui/toast';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { mapProductData, getAvailable } from '#utils/productCardTransform';
import { brand, headers } from 'config';
import { getUserGuid } from '#utils/UserDetails';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isPreparingToMove: {
    type: Boolean,
    default: false,
  },
  index: {
    type: Number,
    default: 0,
  },
});

const envPropsStore = useEnvPropsStore();
const getSelectedListStore = useGetSelectedListStore();
const deleteItemStore = useDeleteItemStore();
const getAllListsStore = useGetAllListsStore();

const emit = defineEmits(['move-item-click', 'add-to-bag-error']);

const productThumbnailKey = ref(0);
const product = ref({});
let payload;
product.value = mapProductData(props.item);
const { wishlistGuid, isWishlistOwner } = useWishlistDetails();
const { isAuthenticated, isGuestUser } = useWishlists();
const ATBComponent = ref(null);
const alreadyAddedInWishList = ref(true);
const title = computed(() => (alreadyAddedInWishList.value ? 'Remove from wishlist' : 'Add to list'));
const iconName = computed(() => (alreadyAddedInWishList.value ? 'wish-list-added' : 'wish-list-removed'));
const isAddToBagLoading = ref(false);
const specialOffersBadge = ref({});
const { getSpecialOffersBadgeSettings } = useSpecialOffersBadge();
const badgeText = product.value?.pricing?.price?.priceType?.text || '';
specialOffersBadge.value = getSpecialOffersBadgeSettings(badgeText, brand);
const available = ref(false);
available.value = getAvailable(props.item);

const hasMultipleUpc = computed(() => product.value?.hasMultipleUpc);
const checkStockThroughPhoneOnly = computed(() => product.value?.checkStockThroughPhoneOnly);
const productId = computed(() => product.value?.productId);

const productOriginalPrice = computed(() => {
  const price = props.item.product?.price?.originalPrice || props.item.upc?.price?.originalPrice || '';
  return typeof price === 'number' ? price.toFixed(2) : price;
});
const productPrice = computed(() => {
  const price = props.item.price?.retailPrice || props.item.upc?.price?.retailPrice || '';
  return typeof price === 'number' ? price.toFixed(2) : price;
});
const imageBadge = ref({
  enableBadge: false,
  badgeText: '',
});

imageBadge.value = {
  enableBadge: !available.value,
  badgeText: 'This item is currently unavailable.',
};

const itemGuid = computed(() => product.value.id);
const productDetails = computed(() => props.item?.upc);
const removeItemFromWishlistStore = (itemGuid) => {
  fireTag('link', {
    event_name: 'wishlist remove',
    product_brand: [productDetails.value.product?.brand?.name || ''],
    product_color: [productDetails.value.color || ''],
    product_id: [`${productDetails.value.product.id}` || ''],
    product_name: [productDetails.value.product.name || ''],
    product_original_price: [productOriginalPrice.value || ''],
    product_price: [productPrice.value || ''],
    product_quantity: ['1'],
    product_size: [productDetails.value.size || ''],
    product_sub_total: [productPrice.value || ''],
    product_upc: [String(productDetails.value.upcNumber) || ''],
    site_type: envPropsStore.envProps?.deviceType === 'PC' ? 'desktop' : 'mobile',
    product_wishlist_remove_amount: [productPrice.value || ''],
  });
  getSelectedListStore.removeItemFromList(itemGuid);
};
const removeItemFromWishlist = async () => {
  if (!getUserGuid()) return;
  const url = `/xapi/wishlist/v1/lists/${wishlistGuid.value}/items/${itemGuid.value}`;
  await deleteItemStore.deleteItem({
    url,
    itemGuid: itemGuid.value,
    headers,
    listGuid: wishlistGuid.value,
  });
};
const removeItem = async () => {
  alreadyAddedInWishList.value = false;
  // Defer API and state updates so the UI responds to the click instantly
  setTimeout(async () => {
    await removeItemFromWishlist();
    Toast({
      message: 'Removed from list',
      toastContainerId: `product-thumbnail-${itemGuid.value}`,
      placement: 'center',
      duration: 1000,
      onTimeout: () => {
        removeItemFromWishlistStore(itemGuid.value);
      },
    });
  }, 0);
};

const getProductSelectionData = () => {
  const todaysDate = new Date();
  const bagDate = `${todaysDate.getMonth() + 1}/${todaysDate.getDate()}/${todaysDate.getFullYear()}`;
  const trackingCategory = isAuthenticated.value ? 'my-wl-xx-xx.index2' : 'my-wl-xx-xx.nsil_guest';
  let prodSelectionInfo = `cmexplore=-_--_--_--_--_--_--_--_--_--_--_--_--_--_--_--_-store_only_product%26PseudoCat%3D${trackingCategory}%26BagDate%3D${bagDate}`;
  prodSelectionInfo = decodeURIComponent(prodSelectionInfo);
  return prodSelectionInfo;
};

const buttonText = computed(() => {
  if (hasMultipleUpc.value) {
    return 'Make selections';
  }
  if (checkStockThroughPhoneOnly.value) {
    return 'Call to order';
  }
  return 'Add to bag';
});

const pseudoCategoryId = computed(() => {
  if (isAuthenticated.value) {
    return 'my-wl-xx-xx.mylist_pdp';
  }
  if (isGuestUser.value) {
    return 'my-wl-xx-xx.index3_pdp';
  }
  return 'my-wl-xx-xx.nsil_guest_pdp';
});

const onAddToBag = () => {
  isAddToBagLoading.value = true;
  // Yield to the main thread to immediately paint the loading spinner, improving INP
  setTimeout(() => {
    if (brand === 'bcom') {
      payload = {
        item: {
          quantity: 1,
          listType: 'WISH_LIST',
          source: 'WSLA2B',
          upcId: props.item?.upc?.id,
          upcNumber: props.item?.upc?.upcNumber,
          deliveryOption: available.value ? 'ship' : 'pickupInStore',
          trackingCategory: 'mi-wl-xx-xx',
        },
      };
    } else {
      payload = {
        item: {
          responsiveBagAddEnabled: getAllListsStore.allListsOfUser?.KillSwitches?.responsiveBagAddEnabled,
          quantity: 1,
          upcId: props.item?.upc?.id,
          upcNumber: props.item?.upc?.upcNumber,
          listType: 'WISH_LIST',
          trackingCategory: isAuthenticated.value ? 'my-wl-xx-xx.index2' : 'my-wl-xx-xx.nsil_guest',
          source: 'WSLA2B',
          prodSelectionInfo: getProductSelectionData(),
        },
      };
    }
    const options = {
      useModal: brand === 'mcom',
    };

    addOrUpdateItemToBag(payload, options)
      .then((atbConfirmation) => {
        ATBComponent.value = atbConfirmation.component;
      })
      .catch((error) => {
        const errorMessage = error?.cause?.message || 'An error occurred while adding the item to the bag.';
        emit('add-to-bag-error', errorMessage);
      })
      .finally(() => {
        isAddToBagLoading.value = false;
      });
  }, 0);
};

const onCheckStockOrSelectAndAdd = () => {
  isAddToBagLoading.value = true;
  // Defer navigation to unblock the click interaction
  setTimeout(() => {
    window.location.href = `/shop/product/?ID=${productId.value}&PseudoCat=${pseudoCategoryId.value}`;
  }, 0);
};

const handleProductAction = () => {
  if (hasMultipleUpc.value || checkStockThroughPhoneOnly.value) {
    onCheckStockOrSelectAndAdd();
  } else {
    onAddToBag();
  }
};

onMounted(() => {
  productThumbnailKey.value += 1;
});
</script>
<style lang="scss" scoped brand="mcom">
@import '@csgIcons/heart-red';
@import '@csgIcons/heart-white-light-border';

.wish-list-added {
  @extend .heart-red;
}

.wish-list-removed {
  @extend .heart-white-light-border;
}
</style>

<style lang="scss" scoped brand="bcom">
@import '@csgIcons/heart-pink-filled';
@import '@csgIcons/heart-white-filled';

.wish-list-added {
  @extend .heart-pink-filled;
}

.wish-list-removed {
  @extend .heart-white-filled;
}
</style>
<style lang="scss" scoped>
.product-card-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  .product-item {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .wishlist-heart-small-card,
  .wishlist-heart-large-card {
    position: absolute;
  }

  .wishlist-heart-small-card {
    top: rem-calc(4);
    right: rem-calc(4);
  }

  .wishlist-heart-large-card {
    top: rem-calc(8);
    right: rem-calc(8);
  }

  .button-container {
    margin-top: auto;
    flex-shrink: 0;
  }

  .product-description {
    flex-grow: 1;
  }

  :deep(.badge-container) {
    white-space: break-spaces;
  }
}
</style>
